// src/features/files/upload/UploadProgressList.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  ErrorOutline as ErrorIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';

import { formatFileSize } from '../utils/formatFileSize';

function UploadRow({ item, onDismiss }) {
  const { t } = useTranslation(['files', 'common']);
  const StatusIcon = item.status === 'done' ? CheckCircleIcon : item.status === 'error' ? ErrorIcon : FileIcon;
  const color = item.status === 'done' ? 'success' : item.status === 'error' ? 'error' : 'action';

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1.5}>
        <StatusIcon color={color} fontSize="small" />
        <Box flex={1} minWidth={0}>
          <Typography variant="body2" noWrap title={item.name}>{item.name}</Typography>
          <Typography variant="caption" color={item.status === 'error' ? 'error' : 'text.secondary'} display="block">
            {item.status === 'error'
              ? item.error
              : item.status === 'queued'
                ? t('files:upload.waiting')
                : `${formatFileSize(item.size)}${item.status === 'uploading' ? ` · ${item.progress} %` : ''}`}
          </Typography>
        </Box>
        {item.status === 'error' && onDismiss && (
          <Tooltip title={t('common:buttons.close')}>
            <IconButton size="small" onClick={() => onDismiss(item.id)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {item.status === 'uploading' && (
        <LinearProgress variant="determinate" value={item.progress} sx={{ mt: 0.75, ml: 4 }} />
      )}
    </Box>
  );
}

/**
 * Uploads from useUploadQueue, one row each with its own progress.
 * `floating` pins a small panel to the bottom right of the screen, for pages
 * and tabs that list files: the same place on every page, and nothing moves.
 * `inline` renders in place, for dialogs and form fields. It only shows uploads
 * still running or failed, since the chip or selected file that appears when
 * an upload is done already says so.
 */
export default function UploadProgressList({ items, onDismiss, onClose, variant = 'inline', sx }) {
  const { t } = useTranslation(['files', 'common']);
  const shown = variant === 'inline' ? items.filter(item => item.status !== 'done') : items;
  if (!shown.length) return null;

  const active = shown.filter(item => item.status === 'queued' || item.status === 'uploading').length;
  const rows = (
    <Stack spacing={1.5}>
      {shown.map(item => <UploadRow key={item.id} item={item} onDismiss={onDismiss} />)}
    </Stack>
  );

  if (variant === 'inline') {
    return (
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 3, py: 2.5, ...sx }}>
        {rows}
      </Box>
    );
  }

  return (
    <Paper
      elevation={6}
      sx={(theme) => ({
        position: 'fixed',
        right: 24,
        bottom: 24,
        width: 360,
        maxWidth: 'calc(100vw - 48px)',
        zIndex: theme.zIndex.snackbar,
        overflow: 'hidden',
        ...sx,
      })}
    >
      <Box display="flex" alignItems="center" px={2} py={1.5} borderBottom={1} borderColor="divider">
        <Typography variant="subtitle2" flex={1}>
          {active
            ? t('files:upload.uploading_count', { count: active })
            : t('files:upload.finished')}
        </Typography>
        {!active && onClose && (
          <Tooltip title={t('common:buttons.close')}>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box px={2} py={1.5} sx={{ maxHeight: 320, overflowY: 'auto' }}>{rows}</Box>
    </Paper>
  );
}
